import { v4 as uuidV4 } from 'uuid';

import { GenericActivityListSort } from 'src/models/genericActivityList';

const normalizeQueryIdentifierPayload = (value: unknown): unknown => {
	if (Array.isArray(value)) {
		return value.map((item) => normalizeQueryIdentifierPayload(item));
	}

	if (value instanceof Date) {
		return value.toISOString();
	}

	if (value !== null && typeof value === 'object') {
		return Object.keys(value as Record<string, unknown>)
			.sort()
			.reduce<Record<string, unknown>>((normalizedValue, key) => {
				const normalizedProperty = normalizeQueryIdentifierPayload((value as Record<string, unknown>)[key]);

				if (normalizedProperty !== undefined) {
					normalizedValue[key] = normalizedProperty;
				}

				return normalizedValue;
			}, {});
	}

	return value;
};

const createStableQueryIdentifier = (name: string, payload: unknown): string => {
	return `${name}#${JSON.stringify(normalizeQueryIdentifierPayload(payload))}`;
};

abstract class AbstractQuery {
	public apiVersion = 1;

	protected cacheable = true;
	public customPayload: Array<
		Record<
			string,
			Array<never> | Record<string, unknown> | boolean | number | number[] | string | string[] | undefined
		>
	> = [];
	public group?: Record<
		string,
		Array<never> | Record<string, unknown> | boolean | number | string | string[] | undefined
	> = {};
	protected ignoreCachedResult = false;
	public limit?: number;
	public page?: number;
	public parameters: Array<
		Record<
			string,
			Array<never> | Record<string, unknown> | boolean | number | number[] | string | string[] | undefined
		>
	> = [];
	processId: string = uuidV4();
	public sort?: Array<GenericActivityListSort> = [];
	protected subscribeForUpdates = true;

	static fromJSON<T>(serializedObject: string): T {
		const parsedObject = JSON.parse(serializedObject) as object;

		return Object.assign(Object.create(this.prototype) as object, parsedObject) as T;
	}

	isCacheable(): boolean {
		return this.cacheable;
	}

	shouldIgnoreCachedResult(): boolean {
		return this.ignoreCachedResult;
	}

	shouldSubscribeForUpdates(): boolean {
		return this.subscribeForUpdates;
	}

	protected disableSubscriptionsWhileSearchIsActive(...searchTerms: Array<string | undefined>): void {
		if (
			searchTerms.some((searchTerm) => {
				return typeof searchTerm === 'string' && searchTerm.trim() !== '';
			})
		) {
			this.subscribeForUpdates = false;
		}
	}

	protected createStableIdentifier(additionalPayload: Record<string, unknown> = {}): string {
		return createStableQueryIdentifier(this.name, {
			additionalPayload,
			apiVersion: this.apiVersion,
			customPayload: this.customPayload,
			group: this.group,
			limit: this.limit,
			page: this.page,
			parameters: this.parameters,
			sort: this.sort,
		});
	}

	uniqueQueryIdentifier(): string {
		return this.createStableIdentifier();
	}

	abstract readonly name: string;
}

class QueryRequest {
	constructor(public queryRequestURL: string) {}
}

export type SerializedQuery = {
	query: string;
	type: string;
};

export { AbstractQuery, QueryRequest };
